/**
 * Authentication API service.
 * Handles login, logout, token refresh, and user info.
 */

import { API_CONFIG } from '../config';
import { useAuthStore, type User } from '../stores/authStore';

const API_BASE = API_CONFIG.REST_BASE;

interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

interface ApiError {
  detail: string;
}

/**
 * Login with email and password.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for refresh token
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.detail || 'Login failed');
  }

  return response.json();
}

/**
 * Logout and clear session.
 */
export async function logout(): Promise<void> {
  const { accessToken } = useAuthStore.getState();

  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
    });
  } catch (error) {
    console.warn('Logout request failed:', error);
  }

  // Always clear local auth state
  useAuthStore.getState().clearAuth();
}

/**
 * Refresh the access token using the refresh token cookie.
 */
export async function refreshToken(): Promise<LoginResponse | null> {
  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send refresh token cookie
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  } catch (error) {
    console.warn('Token refresh failed:', error);
    return null;
  }
}

/**
 * Get current user info.
 */
export async function getCurrentUser(): Promise<User | null> {
  const { accessToken } = useAuthStore.getState();

  if (!accessToken) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  } catch (error) {
    console.warn('Get current user failed:', error);
    return null;
  }
}

/**
 * Check if stored auth is still valid.
 * Attempts to refresh if token might be expired.
 */
export async function validateAuth(): Promise<boolean> {
  const { accessToken, isAuthenticated } = useAuthStore.getState();
  const { setAuth, clearAuth, setLoading } = useAuthStore.getState();

  setLoading(true);

  // If no stored auth, try refresh
  if (!isAuthenticated || !accessToken) {
    const refreshResult = await refreshToken();
    if (refreshResult) {
      setAuth(refreshResult.user, refreshResult.access_token);
      return true;
    }
    clearAuth();
    return false;
  }

  // Validate existing token
  const user = await getCurrentUser();
  if (user) {
    setLoading(false);
    return true;
  }

  // Token invalid, try refresh
  const refreshResult = await refreshToken();
  if (refreshResult) {
    setAuth(refreshResult.user, refreshResult.access_token);
    return true;
  }

  clearAuth();
  return false;
}

/**
 * Create an authenticated fetch function.
 * Automatically adds Authorization header.
 */
export function createAuthenticatedFetch() {
  return async (url: string, options: RequestInit = {}): Promise<Response> => {
    const { accessToken } = useAuthStore.getState();

    const headers = new Headers(options.headers);
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };
}
